import React, { useMemo } from "react";
import { Modal } from "../Modal/Modal";
import {
  ShortcutDef,
  isMacPlatform,
} from "@hooks/useKeyboardShortcuts";

interface ShortcutsHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  shortcuts: ShortcutDef[];
}

/** Converte "mod+s" em teclas legíveis (["⌘", "S"] no Mac; ["Ctrl", "S"] no resto). */
function formatCombo(combo: string, mac: boolean): string[] {
  return combo.split("+").map((raw) => {
    const k = raw.trim().toLowerCase();
    if (k === "mod") return mac ? "⌘" : "Ctrl";
    if (k === "shift") return mac ? "⇧" : "Shift";
    if (k === "esc") return "Esc";
    if (k === "enter") return mac ? "return" : "Enter";
    if (k === "?") return "?";
    return raw.length === 1 ? raw.toUpperCase() : raw;
  });
}

/**
 * Modal de ajuda com a lista de atalhos, alimentado pela MESMA lista que
 * registra os atalhos no editor — nunca fica dessincronizado.
 */
const ShortcutsHelpModal: React.FC<ShortcutsHelpModalProps> = ({
  isOpen,
  onClose,
  shortcuts,
}) => {
  const mac = useMemo(() => isMacPlatform(), []);

  // Agrupar por `group` preservando a ordem de inserção.
  const grupos = useMemo(() => {
    const map = new Map<string, ShortcutDef[]>();
    for (const s of shortcuts) {
      const g = s.group || "Geral";
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(s);
    }
    return Array.from(map.entries());
  }, [shortcuts]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Atalhos de teclado" size="small">
      <p className="text-sm text-base-content/70 -mt-2 mb-4">
        Trabalhe mais rápido sem tirar as mãos do teclado.
      </p>

      <div className="flex flex-col gap-4">
        {grupos.map(([grupo, itens]) => (
          <div key={grupo}>
            <div className="text-xs font-semibold uppercase tracking-wide text-base-content/50 mb-2">
              {grupo}
            </div>
            <ul className="flex flex-col gap-2">
              {itens.map((s) => (
                <li
                  key={s.combo}
                  className="flex items-center justify-between gap-4"
                >
                  <span className="text-sm">{s.description}</span>
                  <span className="flex items-center gap-1 shrink-0">
                    {formatCombo(s.combo, mac).map((tecla, i) => (
                      <kbd key={i} className="kbd kbd-sm">
                        {tecla}
                      </kbd>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Modal>
  );
};

export default ShortcutsHelpModal;
